import React, { useEffect, useRef, useState } from 'react';
import { Crosshair, LocateFixed, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { onCastingMessage, sendCastingRoomMessage } from '@/services/castingWs';
import type { CastingShadow } from '@/services/interviewApi';

// Remote gimbal control for an app guest (DockKit / Insta360 Flow on the
// phone). Mirrors casting's CameraControlPanel gimbal section + the continuous
// pan/tilt protocol: the phone broadcasts device.gimbal.status on the session
// room; commands go to talent-<recordId> as admin.gimbal.command. Renders
// nothing until the phone reports a supported gimbal.
export default function GimbalPanel({ casting }: { casting: CastingShadow }) {
  const [status, setStatus] = useState<{ supported?: boolean; connected?: boolean; tracking?: boolean } | null>(null);
  const [cam, setCam] = useState<{ recordingQuality?: '1080p' | '4K'; isRecording?: boolean; facingMode?: string; autoUpload?: boolean; zoom?: { currentUiZoom: number; maxUiZoom: number; stops: number[]; optical: boolean; warnAboveUiZoom?: number } | null } | null>(null);
  const moveRef = useRef<{ moveId: string; itv: ReturnType<typeof setInterval> } | null>(null);

  useEffect(() => onCastingMessage((m) => {
    if (m.type === 'device.gimbal.status' && String(m.talentId) === String(casting.recordId)) {
      setStatus({ supported: !!m.supported, connected: !!m.connected, tracking: !!m.tracking });
    }
    if (m.type === 'device.gimbal.command_result' && m.ok && m.status) {
      setStatus(s => ({ ...(s || {}), ...m.status }));
    }
    if (m.type === 'camera-state' && String(m.talentId) === String(casting.recordId)) {
      setCam({ recordingQuality: m.recordingQuality, isRecording: !!m.isRecording, facingMode: m.facingMode, autoUpload: m.autoUpload !== false, zoom: m.zoom || null });
    }
  }), [casting.recordId]);

  // Ask the phone for a camera-state report until one lands — the phone may
  // join minutes after this panel mounts, and it only self-reports on change.
  // Gimbal status has no query message, but the app re-broadcasts it after ANY
  // resolved gimbal command — and the native module resolves every command
  // (unknown → ok:false, no physical action) — so an unsupported probe command
  // doubles as "report your gimbal again". Old builds without the module stay
  // silent, which correctly leaves the gimbal section hidden.
  const camRef = useRef(cam);
  camRef.current = cam;
  const statusRef = useRef(status);
  statusRef.current = status;
  useEffect(() => {
    let probes = 0;
    const ask = () => {
      if (!camRef.current) sendCastingRoomMessage(`talent-${casting.recordId}`, { type: 'get-camera-state' });
      if (!statusRef.current && probes++ < 12) {
        sendCastingRoomMessage(`talent-${casting.recordId}`, {
          type: 'admin.gimbal.command', sessionId: casting.sessionId,
          targetParticipantId: casting.recordId, command: 'status_probe', requestId: `probe-${probes}`,
        });
      }
    };
    const t = setTimeout(ask, 1500);
    const itv = setInterval(ask, 5000);
    return () => { clearTimeout(t); clearInterval(itv); };
  }, [casting.recordId, casting.sessionId]);

  const lastZoomSendRef = useRef(0);
  const zoomTrailerRef = useRef<ReturnType<typeof setTimeout>>();
  const setZoom = (uiZoom: number, throttle = false) => {
    setCam(c => c ? { ...c, zoom: c.zoom ? { ...c.zoom, currentUiZoom: uiZoom } : c.zoom } : c);
    const send = () => {
      lastZoomSendRef.current = Date.now();
      sendCastingRoomMessage(`talent-${casting.recordId}`, { type: 'set-camera-zoom', uiZoom });
    };
    if (!throttle) { send(); return; }
    // Slider drag: at most ~8 sends/sec, with a trailing send so the final
    // position always lands even if the drag ends inside the throttle window.
    clearTimeout(zoomTrailerRef.current);
    if (Date.now() - lastZoomSendRef.current > 120) send();
    else zoomTrailerRef.current = setTimeout(send, 130);
  };
  const flipCamera = () => sendCastingRoomMessage(`talent-${casting.recordId}`, { type: 'flip-camera' });

  const setQuality = (quality: '1080p' | '4K') => {
    sendCastingRoomMessage(`talent-${casting.recordId}`, { type: 'set-recording-quality', quality });
    setCam(c => ({ ...(c || {}), recordingQuality: quality }));
  };

  // Same message casting's camera panel sends. Off aborts the in-flight upload
  // and hands the phone's uplink back to the call; the take stays pending and
  // uploads when it's turned back on.
  const setAutoUpload = (enabled: boolean) => {
    sendCastingRoomMessage(`talent-${casting.recordId}`, { type: 'set-auto-upload', enabled });
    setCam(c => ({ ...(c || {}), autoUpload: enabled }));
  };

  const send = (command: string, extra: Record<string, unknown> = {}) => {
    const requestId = crypto.randomUUID ? crypto.randomUUID() : `rq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sendCastingRoomMessage(`talent-${casting.recordId}`, {
      type: 'admin.gimbal.command', sessionId: casting.sessionId,
      targetParticipantId: casting.recordId, command, requestId, ...extra,
    });
    return requestId;
  };

  // TODO(app fix): restore the move_start/keepalive hold protocol once the
  // native continuous-move crash is fixed (jitsi-meet/TICKET_gimbal_command_crash.md).
  // Until then, holding fires the app's DISCRETE nudge commands (~0.3s spins) on
  // a 400ms cadence — same motion, none of the crashing move-path code.
  const stopMove = () => {
    const mv = moveRef.current;
    if (!mv) return;
    moveRef.current = null;
    clearInterval(mv.itv);
  };

  const startMove = (direction: string) => {
    stopMove(); // newest press wins
    const nudge = () => send(direction); // pan_left / pan_right / tilt_up / tilt_down
    nudge();
    const itv = setInterval(nudge, 400);
    moveRef.current = { moveId: direction, itv };
  };

  useEffect(() => () => stopMove(), []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!status?.supported && !cam) return null;

  const btn: React.CSSProperties = { width: 34, height: 34, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'var(--hj-surface-2)', color: 'var(--hj-ink-2)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', touchAction: 'none' };
  const hold = (direction: string) => ({
    onPointerDown: (e: React.PointerEvent) => { e.preventDefault(); startMove(direction); },
    onPointerUp: stopMove, onPointerLeave: stopMove, onPointerCancel: stopMove,
  });

  return (
    <div style={{ padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
      <div className="mono-label flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', marginBottom: 10 }}>
        <Crosshair className="w-3.5 h-3.5" /> Guest camera
        {status?.supported && <span style={{ marginLeft: 'auto', color: status.connected ? 'var(--hj-green)' : 'var(--hj-ink-4)' }}>{status.connected ? (status.tracking ? 'gimbal tracking' : 'gimbal connected') : 'no gimbal'}</span>}
      </div>
      {cam?.zoom && (
        <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
          <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Zoom</span>
          <div className="flex" style={{ marginLeft: 'auto', gap: 4 }}>
            {cam.zoom.stops.map(z => (
              <button key={z} type="button" onClick={() => setZoom(z)}
                title={cam.zoom!.warnAboveUiZoom && z > cam.zoom!.warnAboveUiZoom ? `${z}x — digital zoom, may look soft` : `${z}x`}
                style={{ padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', border: `1px solid ${cam.zoom!.currentUiZoom === z ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`, background: cam.zoom!.currentUiZoom === z ? 'var(--hj-blue)' : 'transparent', color: cam.zoom!.currentUiZoom === z ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink-2)' }}>
                {z}x
              </button>
            ))}
          </div>
          <button type="button" onClick={flipCamera} title="Flip between front and back camera"
            style={{ padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-2)' }}>
            Flip
          </button>
        </div>
      )}
      {cam?.zoom && (
        <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
          <input
            type="range"
            min={1}
            max={cam.zoom.maxUiZoom}
            step={0.1}
            value={cam.zoom.currentUiZoom}
            onChange={e => setZoom(Number(e.target.value), true)}
            className="flex-1"
            style={{ accentColor: 'var(--hj-blue)' }}
            aria-label="Camera zoom"
          />
          <span className="tabular mono-label" style={{ width: 38, textAlign: 'right', color: 'var(--hj-ink-2)' }}>{Number(cam.zoom.currentUiZoom).toFixed(1)}x</span>
        </div>
      )}
      {cam && (() => {
        const on = cam.autoUpload !== false;
        return (
          <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
            <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Auto-upload</span>
            <button type="button" onClick={() => setAutoUpload(!on)}
              title={on ? "On — turn off to free the guest's uplink for the call (in-flight upload aborts; the take stays pending)" : "Off — turn on to upload the guest's pending takes"}
              style={{ marginLeft: 'auto', padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', border: `1px solid ${on ? 'var(--hj-green)' : 'var(--hj-hairline)'}`, background: on ? 'var(--hj-green)' : 'transparent', color: on ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink-2)' }}>
              {on ? 'On' : 'Off'}
            </button>
          </div>
        );
      })()}
      {cam && (
        <div className="flex items-center gap-2" style={{ marginBottom: status?.supported && status.connected ? 12 : 0 }}>
          <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Quality</span>
          <div className="flex" style={{ marginLeft: 'auto', gap: 4 }}>
            {(['1080p', '4K'] as const).map(q => (
              <button key={q} type="button" disabled={cam.isRecording} onClick={() => setQuality(q)} title={cam.isRecording ? 'Applies to the next take — stop recording first' : `Record in ${q}`}
                style={{ padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, fontFamily: 'inherit', cursor: cam.isRecording ? 'not-allowed' : 'pointer', opacity: cam.isRecording ? 0.5 : 1, border: `1px solid ${cam.recordingQuality === q ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`, background: cam.recordingQuality === q ? 'var(--hj-blue)' : 'transparent', color: cam.recordingQuality === q ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink-2)' }}>
                {q}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Pan/tilt and tracking only exist when a gimbal is actually connected.
          A disconnected one used to leave a dead D-pad on screen. */}
      {status?.supported && status.connected && (
      <div className="flex items-center gap-3">
        {/* D-pad — press and hold */}
        <div className="grid" style={{ gridTemplateColumns: '34px 34px 34px', gap: 4 }}>
          <span /><button type="button" style={btn} {...hold('tilt_up')} title="Tilt up"><ChevronUp className="w-4 h-4" /></button><span />
          <button type="button" style={btn} {...hold('pan_left')} title="Pan left"><ChevronLeft className="w-4 h-4" /></button>
          <button type="button" style={btn} onClick={() => send('recenter')} title="Recenter"><LocateFixed className="w-4 h-4" /></button>
          <button type="button" style={btn} {...hold('pan_right')} title="Pan right"><ChevronRight className="w-4 h-4" /></button>
          <span /><button type="button" style={btn} {...hold('tilt_down')} title="Tilt down"><ChevronDown className="w-4 h-4" /></button><span />
        </div>
        <div className="flex flex-col gap-2 flex-1">
          <button type="button" onClick={() => send(status.tracking ? 'stop_tracking' : 'start_tracking')}
            style={{ padding: '9px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', border: `1px solid ${status.tracking ? 'var(--hj-hairline)' : 'var(--hj-blue)'}`, background: status.tracking ? 'transparent' : 'var(--hj-blue)', color: status.tracking ? 'var(--hj-ink-2)' : 'oklch(0.18 0.005 70)' }}>
            {status.tracking ? 'Stop tracking' : 'Track me'}
          </button>
          <p className="m-0" style={{ fontSize: 11, color: 'var(--hj-ink-4)', lineHeight: 1.4 }}>Hold arrows to pan/tilt the guest's camera.</p>
        </div>
      </div>
      )}
    </div>
  );
}
